import { RailTitle } from "molecules/RailTitle";
import { useClassNames } from "@/utils/classNames";

import { RailProps, RailVariant } from "./types";
import styles from "./Rail.module.scss";

/**
 * Rail component to display a title and list of tiles.
 *
 * @param variant - The type of the rail, defaults to Hero34
 * @param title - Optional title for the rail
 * @param icon - Optional icon to display alongside the title
 * @param snap - If the tiles should free scroll or snap to the beginning of the rail
 * @param className - Additional CSS class for styling
 * @param children - Tile children
 * @param attrs - Additional HTML attributes for the div element
 * @returns A rail component
 *
 * @example
 * ```tsx
 * <Rail
 *   variant={RailVariant.Hero34}
 *   title="Featured Content"
 *   icon={StreamingIcons.Lock}
 * >
 *   <Tile34 asset={assetResponse} format={Tile34Format.VOD} />
 * </Rail>
 * ```
 */
const Rail = ({
  variant = RailVariant.Hero34,
  title,
  icon,
  snap = false,
  className,
  children,
  ...attrs
}: RailProps) => {
  const cn = useClassNames(
    styles,
    styles["rail"],
    styles[`variant-${variant}`],
    className,
  );
  const tileContainerCn = useClassNames(
    styles,
    styles["tile-container"],
    snap && styles["snap"],
  );

  return (
    <div className={cn} {...attrs}>
      {title && <RailTitle variant={variant} title={title} icon={icon} />}
      <div className={tileContainerCn}>{children}</div>
    </div>
  );
};

export { Rail };
